/* eslint-disable no-param-reassign, no-unused-vars */

import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';

import GameModel from 'Models/GameModel';
import RegionModel from 'Models/RegionModel';
import MutationType from 'Models/MutationTypeModel';
import AppModel from 'Models/AppModel';
import { CashbackModel } from 'Models/CashbackModel';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import criteoAnalytics from 'Root/drivers/criteoAnalytics';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import CurrencyModel from 'Models/CurrencyModel';
import { round } from 'Root/helpers/tools';
import tooltip from './modules/tooltip';
import products from './modules/products';
import notifications from './modules/notifications';
import onlineUsers from './modules/onlineUsers';
import users from './modules/users';

const { API_URI } = AppModel;

Vue.use(Vuex);

export default function createStore(context = {}) {
  const global = typeof window === 'undefined' ? context : window;

  return new Vuex.Store({
    modules: {
      products,
      notifications,
      tooltip,
      onlineUsers,
      users,
    },

    state: () => ({
      httpErrorCode: null,
      adminSignedAsUser: false,
      connectionInfo: null,
      user: null,
      userReactiveStates: {
        email_verify_later: false,
      },
      activeGame: null,
      activeRegion: null,
      userDevice: {
        innerWidth: 375,
        innerHeight: 667,
        platform: global?._skConfig?.platform || 'web',
        platformFromUA: null,
        isHover: false,
        isCoarse: true,
        isTouch: false,
        isSafari: false,
      },
      isVideoShowing: false,
      screenHolder: null,
      backgrounds: [],

      catalogJSONData: null,
      catalogProducts: {},
      catalogProductsMarkup: {},

      countryCode: null,

      /* eslint-disable max-len */
      reviews: [
        {
          title: 'Awesome! Just Awesome!',
          stars: 5,
          review: 'Staff is super nice and replies extremely quickly. The pro I work with is really nice and gets stuff done quick! Would highly recommend this company! They are a little expensive not going to lie, but it is worth it depending on how much you love the game your playing!',
          author: 'Leali23',
        },
        {
          title: 'As Good As It Gets',
          stars: 5,
          review: 'Fantastic service! Quick and professional. Players are really good and have great personalities. Cost is also way better than competitors.',
          author: 'nikosmaridakis',
        },
        {
          title: 'Awesome',
          stars: 5,
          review: '“I’ve had the pleasure of working with SkyCoach, and I must say they are exceptional. Their communication is fast and efficient, making the entire experience a breeze. The product they offer is nothing short of excellent, meeting all my expectations and more. I highly recommend SkyCoach for their outstanding service and top-notch products.”',
          author: 'Gil2326',
        },
        {
          title: 'Amazing services',
          stars: 5,
          review: 'SkyCoach has great value and customer service. They are always on top of anything I have ordered from them.',
          author: 'MrIs82',
        },
        {
          title: '5 stars',
          stars: 5,
          review: 'Amazing service only had one issue but every time I get a pro, they get the job done and easy. No cheating no hacking, absolute best thing I ever discover when returning back to the game',
          author: 'Butterton3121',
        },
        {
          title: 'Awesome Gaming Service!',
          stars: 5,
          review: 'Have been using SkyCoach.gg for over a year now, and I have to say they have been excellent with my gaming needs. They send great communication on invites to play with them, and wait times are within the stated time. I will keep using them as long as they are around.',
          author: 'Cyro213',
        },
        {
          title: 'Great Service!',
          stars: 5,
          review: 'I have used SkyCoach a number of times and have been very pleased! They are quick to respond with excellent pros!',
          author: 'jrogers180',
        },
        {
          title: 'perfect experiencie',
          stars: 5,
          review: 'Quick, easy and Cheap!',
          author: 'emilychen_84hazybcwu',
        },
        {
          title: 'Cool service',
          stars: 5,
          review: 'Best support I’ve ever seen ? Keep going folks!',
          author: 'Jeffree Purdeyhvxo',
        },
        {
          title: '10/10, would use again.',
          stars: 5,
          review: 'I was worried first, but they did good and finished faster than I think.',
          author: 'Terani Loaldaykkvg',
        },
      ],

      /* eslint-disable max-len */
      accountReviews: [
        {
          title: 'Exactly what I was looking for!',
          stars: 5,
          review: 'I’ve been searching for an account like this for weeks, and finally found it here. The whole process took less than five minutes, and I was in the game instantly. No weird verification steps or hidden fees—just fast, smooth service.',
          author: 'AnonymousLich',
        },
        {
          title: 'Legit and super fast!',
          stars: 5,
          review: 'Didn’t expect to get my account within seconds, but here we are. Everything was exactly as described, no surprises. I even double-checked the login details, just in case, but nope—100% legit. Definitely worth it.',
          author: 'AnonymousTitan',
        },
        {
          title: 'Good price, solid service',
          stars: 4,
          review: 'This was the best price I found for the account I wanted, and the purchase was quick. If you’re on the fence, I’d say go for it. I had zero issues, and I’m pretty picky about where I buy.',
          author: 'AnonymousRogue',
        },
        {
          title: 'Honestly impressed',
          stars: 5,
          review: 'I don’t usually leave reviews, but I feel like I should. The account was delivered instantly, and everything was in place. No issues logging in, no drama. Just a clean, hassle-free purchase. Appreciate the smooth experience!',
          author: 'AnonymousWraith',
        },
        {
          title: 'First time buying—no regrets!',
          stars: 5,
          review: 'I was super skeptical at first because, let’s be real, buying accounts online isn’t always safe. But this place is solid. Account came through immediately, all skins and items were exactly as shown. Feels good to get what I actually paid for!',
          author: 'AnonymousDragon',
        },
        {
          title: 'Customer support is amazing',
          stars: 5,
          review: 'I had some dumb questions before buying, and the support team answered everything in detail without rushing me. Ended up buying, and the account was delivered instantly. Great customer service, really made a difference!',
          author: 'AnonymousValkyrie',
        },
        {
          title: 'Got what I paid for',
          stars: 5,
          review: 'Everything worked, and I got the account fast. I guess the only thing I’d change is maybe a bit more details on what’s included, but overall, very happy with my purchase. Solid experience.',
          author: 'AnonymousAssassin',
        },
        {
          title: 'Didn’t expect it to be this easy',
          stars: 5,
          review: 'I’ve bought accounts before, and usually, there’s always some kind of issue—wrong details, slow delivery, something annoying. But this was seamless. Paid, received, logged in, done. Will definitely be back.',
          author: 'AnonymousRevenant',
        },
        {
          title: 'A legit site, finally!',
          stars: 5,
          review: 'After getting scammed on another platform, I was hesitant to try again. But this site proved me wrong. The process was smooth, the account was real, and I actually got what I ordered. Finally, a place I can trust.',
          author: 'AnonymousWarden',
        },
        {
          title: 'Good experience overall',
          stars: 4,
          review: 'Got my account quickly, everything works fine. Only thing I’d say is maybe add more detailed descriptions on some listings, but other than that, this was a great experience. Would recommend.',
          author: 'AnonymousOracle',
        },
        {
          title: 'So much better than expected',
          stars: 5,
          review: 'I thought this would take hours, but it was almost instant. The account had everything listed, no missing skins or items. I’m impressed by how professional and straightforward the whole thing was.',
          author: 'AnonymousBeast',
        },
        {
          title: 'Not bad at all',
          stars: 5,
          review: 'Decent selection, fair prices, and fast delivery. My only suggestion would be to improve search filters a bit, but otherwise, this was a smooth experience. Would buy here again.',
          author: 'AnonymousStalker',
        },
        {
          title: 'No complaints, just great service',
          stars: 5,
          review: 'Everything was exactly as advertised. It’s rare to find a site that delivers exactly what it promises, but this one does. If you’re thinking about buying, just do it—you won’t regret it.',
          author: 'AnonymousSpecter',
        },
        {
          title: 'Better than dealing with resellers',
          stars: 5,
          review: 'I used to buy accounts from individual sellers, but it was always a gamble. This site removes the risk—fast delivery, guaranteed details, no stress. Definitely a game-changer for me.',
          author: 'AnonymousOverlord',
        },
      ],

      /* eslint-enable max-len */
      postPayData: {},
      characterIds: [],

      tabManager: {
        // Все задаем и импортим duelJS в entry-client
        channel: null, // общий канал duelJS
        isActiveTab: null, // проверка на активный таб. Если после ф5 фокус на консоли, то вкладка будет не главной, если она не единственная!!!
        listenEvent: null, // (name, callback) => {...}, // вешаем прослушку на ивент
        removeListener: null, // (name) => {...}, // убираем прослушку на ивент
        broadcast: null, // (name, data, activeTabCallback) => {...}, // шлем ивенты в не активные вкладки и вызываем callback для активной
        emit: null, // (name, data) => {...}, // шлем ивенты во все вкладки
      },

      usdToCurrencyRatio: {
        [CurrencyModel.EUR.id]: null,
        [CurrencyModel.USD.id]: 1,
      },
    }),

    actions: {
      getUser({ commit }) {
        return axios.get('/shop/user').then(({ data }) => {
          const { user } = data;

          commit('mutateUser', user);

          const signedAsUser = 'localStorage' in window && window.localStorage.getItem('signed-as-user');
          if (!signedAsUser) {
            const intercomData = {
              app_id: window.intercomSettings.app_id,
              user_id: user.id,
              user_ip: user.ip,
              email: user.email,
              created_at: user.created_at,
              user_hash: user.intercom_hash,
              paid_orders_count: user?.customer_counters?.paid_orders_count,
              paid_orders_sum: user?.customer_counters?.paid_orders_sum,
              customer_popular_games: user?.customer_popular_games
                ?.map((gameId) => GameModel.ID_TO_DATA[gameId].title).join(', '),
            };

            IntercomApiDriver.update(intercomData);
            criteoAnalytics.fireEmail(user.email);
          }

          if (typeof window !== 'undefined') {
            googleTagAnalytics.setUserData({ email: user.email });
          }
        });
      },

      updateReviewsImagesByProducts({ state, commit }) {
        if (state.reviews.find((r) => r.foreground)) {
          return;
        }

        const productIds = [];
        state.reviews.forEach((review) => {
          if (review.productId) {
            productIds.push(review.productId);
          }
        });

        if (!productIds.length) {
          return;
        }

        axios.get(`${API_URI}/product/list_all?with_in_id=[${productIds}]&limit=${productIds.length}`)
          .then(({ data }) => {
            const reviewsDataByProductId = {};
            if (data.result?.length) {
              data.result.forEach((product) => {
                const foregroundImage = product.picture_item_small || product.picture_item;

                reviewsDataByProductId[product.id] = {
                  // background: product.picture_bg ? `/storage${product.picture_bg}` : '/images/temp/card-bg.jpg',
                  foreground: foregroundImage ? `/storage${foregroundImage}` : null,
                };
              });
            }

            commit('mutateReviews', reviewsDataByProductId);
          });
      },

      showScreenHolder({ commit }, screenHolder) {
        commit('mutateScreenHolder', screenHolder);
      },

      hideScreenHolder({ commit }) {
        commit('mutateScreenHolder', null);
      },

      logout() {
        return axios.post('/shop/user/logout');
      },

      setDefaultBackground({ state, commit }, { isNeutral, custom } = {}) {
        let backgrounds = [];
        let directoryName;

        if (custom || isNeutral) {
          if (custom) {
            directoryName = custom;
          } else {
            directoryName = isNeutral ? 'neutral' : state.activeGame.id;
          }

          backgrounds = [
            {
              default: `/images/games/backgrounds/${directoryName}/bg-1920.jpg`,
              sources: [
                {
                  media: '(-webkit-min-device-pixel-ratio: 3)',
                  srcset: `/images/games/backgrounds/${directoryName}/bg-750.avif 1400w,
                   /images/games/backgrounds/${directoryName}/bg-1100.avif 1920w,
                   /images/games/backgrounds/${directoryName}/bg-1400.avif 2800w`,
                  type: 'image/avif',
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 2)',
                  srcset: `/images/games/backgrounds/${directoryName}/bg-750.avif 1100w,
                   /images/games/backgrounds/${directoryName}/bg-1100.avif 1400w,
                   /images/games/backgrounds/${directoryName}/bg-1400.avif 1920w,
                   /images/games/backgrounds/${directoryName}/bg-1920.avif 2800w`,
                  type: 'image/avif',
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 1)',
                  srcset: `/images/games/backgrounds/${directoryName}/bg-375.avif 375w,
                   /images/games/backgrounds/${directoryName}/bg-750.avif 750w,
                   /images/games/backgrounds/${directoryName}/bg-1100.avif 1100w,
                   /images/games/backgrounds/${directoryName}/bg-1400.avif 1400w,
                   /images/games/backgrounds/${directoryName}/bg-1920.avif 1920w,
                   /images/games/backgrounds/${directoryName}/bg-2800.avif 2800w`,
                  type: 'image/avif',
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 3)',
                  srcset: `/images/games/backgrounds/${directoryName}/bg-750.jpg 1400w,
                    /images/games/backgrounds/${directoryName}/bg-1100.jpg 1920w,
                    /images/games/backgrounds/${directoryName}/bg-1400.jpg 2800w`,
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 2)',
                  srcset: `/images/games/backgrounds/${directoryName}/bg-750.jpg 1100w,
                    /images/games/backgrounds/${directoryName}/bg-1100.jpg 1400w,
                    /images/games/backgrounds/${directoryName}/bg-1400.jpg 1920w,
                    /images/games/backgrounds/${directoryName}/bg-1920.jpg 2800w`,
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 1)',
                  srcset: `/images/games/backgrounds/${directoryName}/bg-375.jpg 375w,
                    /images/games/backgrounds/${directoryName}/bg-750.jpg 750w,
                    /images/games/backgrounds/${directoryName}/bg-1100.jpg 1100w,
                    /images/games/backgrounds/${directoryName}/bg-1400.jpg 1400w,
                    /images/games/backgrounds/${directoryName}/bg-1920.jpg 1920w,
                    /images/games/backgrounds/${directoryName}/bg-2800.jpg 2800w`,
                },
              ],
              shouldShowGradient: true,
              isSmall: true,
            },
          ];
        } else {
          backgrounds = [
            {
              default: `${state.activeGame.urlBackground}-1920.jpg`,
              sources: [
                {
                  media: '(-webkit-min-device-pixel-ratio: 3)',
                  srcset: `${state.activeGame.urlBackground}-750.avif 1400w,
                   ${state.activeGame.urlBackground}-1100.avif 1920w,
                   ${state.activeGame.urlBackground}-1400.avif 2800w`,
                  type: 'image/avif',
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 2)',
                  srcset: `${state.activeGame.urlBackground}-750.avif 1100w,
                   ${state.activeGame.urlBackground}-1100.avif 1400w,
                   ${state.activeGame.urlBackground}-1400.avif 1920w,
                   ${state.activeGame.urlBackground}-1920.avif 2800w`,
                  type: 'image/avif',
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 1)',
                  srcset: `${state.activeGame.urlBackground}-375.avif 375w,
                   ${state.activeGame.urlBackground}-750.avif 750w,
                   ${state.activeGame.urlBackground}-1100.avif 1100w,
                   ${state.activeGame.urlBackground}-1400.avif 1400w,
                   ${state.activeGame.urlBackground}-1920.avif 1920w,
                   ${state.activeGame.urlBackground}-2800.avif 2800w`,
                  type: 'image/avif',
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 3)',
                  srcset: `${state.activeGame.urlBackground}-750.jpg 1400w,
                    ${state.activeGame.urlBackground}-1100.jpg 1920w,
                    ${state.activeGame.urlBackground}-1400.jpg 2800w`,
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 2)',
                  srcset: `${state.activeGame.urlBackground}-750.jpg 1100w,
                    ${state.activeGame.urlBackground}-1100.jpg 1400w,
                    ${state.activeGame.urlBackground}-1400.jpg 1920w,
                    ${state.activeGame.urlBackground}-1920.jpg 2800w`,
                },
                {
                  media: '(-webkit-min-device-pixel-ratio: 1)',
                  srcset: `${state.activeGame.urlBackground}-375.jpg 375w,
                    ${state.activeGame.urlBackground}-750.jpg 750w,
                    ${state.activeGame.urlBackground}-1100.jpg 1100w,
                    ${state.activeGame.urlBackground}-1400.jpg 1400w,
                    ${state.activeGame.urlBackground}-1920.jpg 1920w,
                    ${state.activeGame.urlBackground}-2800.jpg 2800w`,
                },
              ],
              shouldShowGradient: true,
              isSmall: true,
            },
          ];
        }

        // костылек для новых пикч
        const oldGameIds = [
          GameModel.DESTINY.id,
          GameModel.WOW_BC.id,
          GameModel.COD_MW.id,
          GameModel.NEW_WORLD.id,
        ];

        if (directoryName !== 'neutral' && !custom && !oldGameIds.includes(state.activeGame.id)) {
          backgrounds[0].isNewGame = true;
        }

        commit('mutateBackgrounds', {
          backgrounds,
        });
      },

      showVideo({ commit }) {
        if (typeof window === 'undefined') {
          return;
        }

        document.body.classList.add('no-scroll');
        commit('mutateVideoShowingState', true);
      },

      hideVideo({ commit }) {
        if (typeof window === 'undefined') {
          return;
        }

        document.body.classList.remove('no-scroll');
        commit('mutateVideoShowingState', false);
      },

      getSeoText({ commit }, fileName) {
        return axios.get(`${API_URI}/seo_files/${fileName}`);
      },

      getCurrencyRate({ commit }) {
        return axios.get(`${API_URI}/currency_rate/ratio/latest`)
          .then(({ data }) => {
            commit('mutateUsdToCurrencyRatio', { currencyId: CurrencyModel.EUR.id, ratio: data });
          });
      },

      setCatalogJSONData({ commit }, data) {
        commit('mutateCatalogJSONData', data);
      },

      fetchCatalogProductsByGameId({ commit }, gameId) {
        return axios.post(`${API_URI}/catalog/products`, { game_ids: [gameId] })
          .then((response) => {
            commit('mutateCatalogProducts', { gameId, newProducts: response.data });
          });
      },
    },

    getters: {
      calcPriceFromUsd: (state, getters) => (regionId, price, shouldFormat = true, precision = 2, useRound = false) => {
        const currencyId = RegionModel.ID_TO_DATA[regionId].currency.id;
        let currencyPrice = currencyId in state.usdToCurrencyRatio
          ? state.usdToCurrencyRatio[currencyId] * price
          : price;

        if (!shouldFormat && useRound) {
          currencyPrice = round(currencyPrice, precision);
        }

        return shouldFormat ? getters.currencyValue(currencyPrice, regionId, precision, useRound) : currencyPrice;
      },

      isAdmin: (state) => (state.user ? +state.user.role === 2 : false),

      isUsersFirstOrder: (state) => !(state.user?.customer_counters?.paid_orders_count > 0),

      activeRegionShortTitle: (state) => (
        state.activeRegion
          ? state.activeRegion.shortTitle // get key by value
          : RegionModel.EU.shortTitle // fallback title
      ),

      currencySymbol: (state) => (regionId) => {
        if (!regionId) {
          regionId = state.activeRegion.id;
        }

        return RegionModel.ID_TO_DATA[regionId].currency.symbol;
      },

      currencyValue: (state, getters) => (amount, regionId = null, precision = 2, useRound = false) => {
        if (!regionId) {
          regionId = state.activeRegion.id;
        }

        const currencySymbol = getters.currencySymbol(regionId);
        const amountSign = amount < 0 ? '-' : '';
        let currency = Math.abs(amount).toFixed(precision);

        if (useRound) {
          currency = round(currency, precision);
        }

        if (regionId === RegionModel.EU.id) {
          return `${amountSign}${currency}${currencySymbol}`;
        }

        return `${amountSign}${currencySymbol}${currency}`;
      },

      userCashback: (state) => {
        if (!state.user) {
          return null;
        }

        const completedOrdersSum = state.user.customer_counters?.completed_orders_sum || 0;

        let rate = 0;

        for (let i = 0; i < CashbackModel.RATE_TABLE.length; i++) {
          if (completedOrdersSum < CashbackModel.RATE_TABLE[i].step) {
            break;
          }
          rate = CashbackModel.RATE_TABLE[i].rate;
        }

        return {
          value_eu: Math.round((state.user?.cashback_balance_in_eur || 0) * 100) / 100,
          value_usa: Math.round((state.user?.cashback_balance || 0) * 100) / 100,
          rate,
        };
      },

      isPlatformIos: (state) => state.userDevice.platform === 'ios',

      isPlatformAndroid: (state) => state.userDevice.platform === 'android',

      isPlatformApp: (state) => state.userDevice.platform === 'ios' || state.userDevice.platform === 'android',
    },

    mutations: {
      mutateUsdToCurrencyRatio(state, { currencyId, ratio }) {
        state.usdToCurrencyRatio[currencyId] = ratio;
      },

      mutateUser(state, user) {
        state.user = user;
      },

      mutateUserFields(state, payload = {}) {
        state.user = { ...state.user, ...payload };
      },

      // eslint-disable-next-line camelcase
      mutateBalance(state, { balance, frozen_balance }) {
        if (typeof balance !== 'undefined') {
          Vue.set(state.user, 'balance', balance);
        }

        // eslint-disable-next-line camelcase
        if (typeof frozen_balance !== 'undefined') {
          Vue.set(state.user, 'frozen_balance', frozen_balance);
        }
      },
      /* eslint-enable camelcase */

      mutateConnectionInfo(state, info) {
        state.connectionInfo = info;
      },

      mutateActiveRegion(state, id) {
        state.activeRegion = RegionModel.ID_TO_DATA[Number.parseInt(id, 10)];
      },

      mutateActiveGame(state, id) {
        // fallback to wow
        if (!GameModel.ID_TO_DATA[id]) {
          id = GameModel.WOW.id;
        }

        if (this._vm.$cookie && this._vm.$cookie.get('activeGame') !== id) {
          this._vm.$cookie.set('activeGame', id);
        }

        if (state.activeGame?.id === id) {
          return;
        }

        state.activeGame = GameModel.ID_TO_DATA[id];
        state.products.activeTag = null;
        state.products.products = [];
      },

      mutateUserDevice(state, data) {
        state.userDevice = { ...state.userDevice, ...data };
      },

      mutateAdminSignedAsUser(state, status) {
        state.adminSignedAsUser = status;
      },

      mutateScreenHolder(state, screenHolder) {
        state.screenHolder = screenHolder;
      },

      mutateUnreadNotifications(state, count) {
        if (state.user) {
          state.user.unread_notifications_count = count;
        }
      },

      mutatePostPayData(state, data) {
        state.postPayData = data;
      },

      mutateCharacterIds(state, data) {
        state.characterIds = data;
      },

      mutateCountryCode(state, data) {
        state.countryCode = data;
      },

      mutateBackgrounds(state, { backgrounds, type }) {
        if (type === MutationType.PUSH) {
          if (Array.isArray(backgrounds)) {
            state.backgrounds = [...state.backgrounds, ...backgrounds];
          } else {
            state.backgrounds.push(backgrounds);
          }
        } else if (type === MutationType.POP) {
          state.backgrounds.pop();
        } else if (Array.isArray(backgrounds)) {
          state.backgrounds = backgrounds;
        }
      },

      mutateVideoShowingState(state, data) {
        state.isVideoShowing = data;
      },

      mutateHttpErrorCode(state, code) {
        state.httpErrorCode = code;
      },

      mutateReviews(state, data) {
        state.reviews = state.reviews.map((review) => (
          data[review.productId] ? { ...review, ...data[review.productId] } : review
        ));
      },

      mutateBoosterFields(state, payload) {
        if (state.user.booster_profile) {
          state.user.booster_profile = { ...state.user.booster_profile, ...payload };
        }
      },

      mutateConfirmRules(state) {
        state.user.booster_profile.is_read_rules = true;
      },

      mutateCatalogJSONData(state, data) {
        state.catalogJSONData = data;
      },

      mutateCatalogProducts(state, { gameId, newProducts }) {
        if (!state.catalogProducts[gameId]) {
          state.catalogProducts[gameId] = [];
        }
        state.catalogProducts[gameId] = [...state.catalogProducts[gameId], ...newProducts.result];
        state.catalogProductsMarkup[gameId] = { ...newProducts?.micromarkup };
      },
    },
  });
}
